import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * CollapsibleCard — reusable accordion-style wrapper.
 *
 * Wraps any block of form fields with a click-to-expand header. The header
 * keeps the same look as the existing white-bordered section cards, so it
 * slots into the listing form without breaking the visual rhythm.
 *
 * Feb-2026 Iter 28 (User-Wunsch 25 May): Beim Aufklappen wird die Card
 * automatisch oben im Viewport positioniert, damit der gesamte Inhalt
 * sichtbar ist (ohne dass der User scrollen muss). Sticky-Header werden
 * via 96 px Offset berücksichtigt.
 *
 * Props:
 *   - title:       string shown in the header (e.g. "Ausstattung")
 *   - icon:        optional lucide icon component
 *   - hint:        optional secondary line under the title
 *   - badge:       optional small right-side badge (e.g. selected-count)
 *   - defaultOpen: initial expanded state (default: false)
 *   - testId:      data-testid root identifier
 */
export default function CollapsibleCard({
    title,
    icon: Icon,
    hint,
    badge,
    defaultOpen = false,
    open: openProp,
    onOpenChange,
    testId,
    headerExtras,
    children,
}) {
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const isControlled = openProp !== undefined;
    const open = isControlled ? openProp : internalOpen;
    const rootRef = useRef(null);
    const prevOpenRef = useRef(open);
    useEffect(() => {
        // Nur scrollen, wenn die Karte gerade aufgeklappt wurde (false → true).
        if (open && !prevOpenRef.current && rootRef.current) {
            // Warten bis React den Body gerendert hat, dann smooth scrollen.
            const t = setTimeout(() => {
                const el = rootRef.current;
                if (!el) return;
                const rect = el.getBoundingClientRect();
                const STICKY_OFFSET = 96; // Header-Höhe + Atemraum
                const targetTop = window.scrollY + rect.top - STICKY_OFFSET;
                window.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });
            }, 60);
            return () => clearTimeout(t);
        }
        prevOpenRef.current = open;
        return undefined;
    }, [open]);
    const toggle = () => {
        const next = !open;
        if (!isControlled) setInternalOpen(next);
        if (onOpenChange) onOpenChange(next);
    };
    return (
        <div ref={rootRef} className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid={testId}>
            <div className="w-full flex items-center gap-3 px-6 py-4 hover:bg-gray-50 transition-colors">
                <button
                    type="button"
                    onClick={toggle}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    data-testid={testId ? `${testId}-toggle` : undefined}
                    aria-expanded={open}
                >
                    {Icon && (
                        <div className="w-9 h-9 bg-[#FFCC00] flex items-center justify-center rounded-sm shrink-0">
                            <Icon className="w-4 h-4 text-[#111827]" strokeWidth={2.5} />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <h2 className="font-display text-lg font-bold leading-tight">{title}</h2>
                        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
                    </div>
                    {badge != null && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FFCC00] text-[#111827] px-2 py-0.5 rounded-sm">
                            {badge}
                        </span>
                    )}
                </button>
                {headerExtras && (
                    <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                        {headerExtras}
                    </div>
                )}
                <button
                    type="button"
                    onClick={toggle}
                    className="shrink-0 p-1 -mr-1 text-gray-400 hover:text-gray-700"
                    aria-label={open ? "Einklappen" : "Ausklappen"}
                    aria-expanded={open}
                >
                    <ChevronDown
                        className={`w-5 h-5 transition-transform ${open ? "rotate-180" : ""}`}
                        aria-hidden="true"
                    />
                </button>
            </div>
            {open && (
                <div className="px-6 pb-6 pt-2 border-t border-gray-100" data-testid={testId ? `${testId}-body` : undefined}>
                    {children}
                </div>
            )}
        </div>
    );
}
